import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorLayout } from '@/Pages/Error';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  image?: string;
  texts?: {
    title: string;
    description: string;
    contactSupport: string;
    tryAgain: string;
  };
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorImage?: string;
}

const SUPPORT_NUMBER_DEFAULT = '551155054750';

/**
 * Global Error Boundary Component
 * Catches React errors and displays a fallback UI with support options
 * Dynamically loads error images based on the build target
 */
export class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Handles opening WhatsApp with error details to contact support
   */
  private handleContactSupport = () => {
    const errorDetails = {
      page: window.location.pathname,
      url: window.location.href,
      timestamp: new Date().toLocaleString('pt-BR'),
      error: this.state.error?.message || 'Erro desconhecido',
      userAgent: navigator.userAgent,
    };

    const supportMessage = `
🚨 *ERRO REPORTADO AUTOMATICAMENTE*

📍 *Página:* ${errorDetails.page}
🕒 *Horário:* ${errorDetails.timestamp}
❌ *Erro:* ${errorDetails.error}

🔗 *URL completa:* ${errorDetails.url}

📱 *Navegador:* ${errorDetails.userAgent}

---
Olá! Ocorreu um erro inesperado no sistema. Podem me ajudar a resolver?
    `.trim();

    const supportWhatsApp = SUPPORT_NUMBER_DEFAULT;
    const whatsappURL = `https://wa.me/${supportWhatsApp}?text=${encodeURIComponent(supportMessage)}`;

    window.open(whatsappURL, '_blank', 'noopener,noreferrer');
  };

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    this.logErrorToService(error, errorInfo);
  }

  /**
   * Logs error to Sentry with additional context
   */
  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
        app: {
          page: window.location.pathname,
        },
      },
    });
  };

  /**
   * Resets the error boundary state
   */
  private handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorImage: undefined,
    });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorLayout
          type='error'
          image={this.props.image}
          title={this.props.texts?.title || 'Oops! Algo deu errado'}
          description={
            this.props.texts?.description ||
            'Ocorreu um erro inesperado. Nossa equipe foi notificada.'
          }
          primaryButton={{
            text: this.props.texts?.tryAgain || 'Tentar novamente',
            onClick: this.handleReset,
          }}
          secondaryButton={{
            text: this.props.texts?.contactSupport || 'Falar com Suporte',
            onClick: this.handleContactSupport,
            icon: 'Communication.Phone',
          }}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}
